(() => {
  'use strict';
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  function initialize(scope) {
    scope.querySelectorAll('[data-brand-mix]').forEach((mix) => {
      if (mix.dataset.ready) return;
      const viewport = mix.querySelector('.rh-brand-mix-window');
      const track = mix.querySelector('.rh-brand-mix-track');
      const group = mix.querySelector('.rh-brand-mix-group');
      const button = mix.querySelector('.rh-brand-mix-pause');
      if (!viewport || !track || !group || !button) return;
      const events = new AbortController();
      const listen = (node, event, handler, options = {}) => node.addEventListener(event, handler, { ...options, signal: events.signal });
      const copies = [group.cloneNode(true), group.cloneNode(true)];
      copies.forEach((copy) => { copy.setAttribute('aria-hidden', 'true'); track.appendChild(copy); });
      track.querySelectorAll('img').forEach((image) => { image.draggable = false; });
      let distance = 0;
      let position = 0;
      let manual = false;
      let resumeAt = 0;
      let pointerDown = false;
      let hovered = false;
      let focused = false;
      let visible = false;
      let drag = null;
      let frame = 0;
      let previousTime = 0;
      const wrap = (value) => distance + ((value - distance) % distance + distance) % distance;
      const hold = () => { manual = true; resumeAt = performance.now() + 1800; position = viewport.scrollLeft; };
      const move = (value) => {
        position = !reduced.matches && distance > 0 ? wrap(value) : Math.max(0, Math.min(value, viewport.scrollWidth - viewport.clientWidth));
        viewport.scrollLeft = position;
      };
      const measure = () => {
        mix.style.setProperty('--rh-mix-width', `${viewport.clientWidth}px`);
        const next = group.getBoundingClientRect().width;
        if (Math.abs(next - distance) < 0.5) return;
        const progress = distance ? ((viewport.scrollLeft % distance) + distance) % distance / distance : 0;
        distance = next;
        move(reduced.matches ? viewport.scrollLeft : distance * (1 + progress));
      };
      const finish = () => {
        pointerDown = false;
        drag = null;
        delete mix.dataset.dragging;
        hold();
      };
      listen(button, 'click', () => {
        const paused = mix.dataset.paused !== 'true';
        mix.dataset.paused = String(paused);
        button.setAttribute('aria-pressed', String(paused));
        button.textContent = paused ? button.dataset.resume : button.dataset.pause;
        if (!paused) { manual = false; resumeAt = 0; position = viewport.scrollLeft; }
      });
      listen(viewport, 'pointerdown', (event) => {
        if (event.button !== 0) return;
        pointerDown = true;
        hold();
        if (event.pointerType !== 'mouse') return;
        drag = { x: event.clientX, position: viewport.scrollLeft, id: event.pointerId };
        viewport.setPointerCapture(event.pointerId);
      });
      listen(viewport, 'pointermove', (event) => {
        if (!drag || drag.id !== event.pointerId) return;
        const offset = event.clientX - drag.x;
        if (Math.abs(offset) < 4 && !mix.dataset.dragging) return;
        event.preventDefault();
        mix.dataset.dragging = 'true';
        move(drag.position - offset);
        resumeAt = performance.now() + 1800;
      });
      listen(viewport, 'pointerup', finish);
      listen(viewport, 'pointercancel', finish);
      listen(viewport, 'lostpointercapture', () => { if (drag) finish(); });
      listen(viewport, 'wheel', hold, { passive: true });
      listen(viewport, 'scroll', () => {
        if (manual || pointerDown) { position = viewport.scrollLeft; resumeAt = performance.now() + 1800; }
      }, { passive: true });
      listen(viewport, 'keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        hold();
        if (event.key === 'Home') move(reduced.matches ? 0 : distance);
        else if (event.key === 'End') move((reduced.matches ? 0 : distance) + Math.max(0, distance - viewport.clientWidth));
        else move(viewport.scrollLeft + (event.key === 'ArrowRight' ? 160 : -160));
      });
      listen(viewport, 'pointerenter', (event) => { if (event.pointerType === 'mouse') hovered = true; });
      listen(viewport, 'pointerleave', (event) => { if (event.pointerType === 'mouse') hovered = false; });
      listen(viewport, 'focusin', () => { focused = true; });
      listen(viewport, 'focusout', (event) => { focused = viewport.contains(event.relatedTarget); });
      const updateMotion = () => {
        button.hidden = reduced.matches;
        move(reduced.matches ? 0 : distance);
        manual = false;
      };
      reduced.addEventListener('change', updateMotion);
      const resize = new ResizeObserver(measure);
      resize.observe(viewport);
      const intersection = new IntersectionObserver((entries) => { visible = entries[0].isIntersecting; }, { rootMargin: '150px' });
      intersection.observe(viewport);
      const tick = (now) => {
        const elapsed = previousTime ? Math.min(now - previousTime, 50) : 0;
        previousTime = now;
        if (manual && now >= resumeAt && !pointerDown) { manual = false; position = viewport.scrollLeft; }
        if (visible && !document.hidden && !reduced.matches && !manual && !pointerDown && !hovered && !focused && mix.dataset.paused !== 'true' && distance > 0) move(position - elapsed * 0.018);
        frame = requestAnimationFrame(tick);
      };
      measure();
      updateMotion();
      mix.dataset.ready = 'true';
      frame = requestAnimationFrame(tick);
      mix.addEventListener('rh-brand-mix:destroy', () => {
        cancelAnimationFrame(frame);
        resize.disconnect();
        intersection.disconnect();
        events.abort();
        reduced.removeEventListener('change', updateMotion);
        copies.forEach((copy) => copy.remove());
        delete mix.dataset.ready;
      }, { once: true });
    });
  }
  initialize(document);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-brand-mix]').forEach((mix) => mix.dispatchEvent(new Event('rh-brand-mix:destroy')));
  });
})();
